import { Injectable, Inject } from '@angular/core';
import moment from 'moment';
import { HttpClient } from '@angular/common/http';
import { BaseHttpService } from '../http/base.http.service';
import { ApiRouterService } from '../http/api.router';
import { ExpeditionStatus, ExpeditionType, ServiceType } from 'src/app/models/enums';
import { IBase, IListExportPage } from 'src/app/models';
import { ITripsFilters } from 'src/app/filters';

@Injectable({
  providedIn: 'root',
})
export class TripsHttpService extends BaseHttpService {
  private baseURL = `trips`;

  constructor(
    @Inject(HttpClient) httpClient: HttpClient,
    private routerService: ApiRouterService,
  ) {
    super(httpClient);
  }

  public getTrips(
    size: number,
    page: number,
    towns: IBase[],
    searchValue?: string
  ) {
    let path = `?page=${page}&size=${size}&expedition_status=${ExpeditionStatus.OPEN},${ExpeditionStatus.READY},${ExpeditionStatus.ONGOING}`;
    if (towns.length > 0) {
      const townsIds = towns.map(town => town.id).join(',');
      path += `&towns=${townsIds}`;
    }
    path +=
      searchValue
        ? `&value=${searchValue}`
        : '';
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getTripsFiltered(
    size: number,
    page: number,
    expeditionClients: IBase[],
    expeditionOperators: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    searchValue?: string,
    expeditionServices?: IBase[],
    expeditionRoutes?: IBase[],
    dateOnly?: boolean,
    from?: Date,
    to?: Date,
    availabilityId?: number | null,
    expeditionDrivers?: IBase[],
    expeditionVehicles?: IBase[],
    expeditionServiceTypes?: ServiceType[]
  ) {
    const filterParams = this.filterParams(
      expeditionClients, expeditionOperators, expeditionTowns, expeditionTypes, expeditionStatus,
      searchValue, expeditionServices, expeditionRoutes, dateOnly, from, to, availabilityId,
      expeditionDrivers, expeditionVehicles, expeditionServiceTypes,
    );
    const path = `/filter?${[`page=${page}`, `size=${size}`, ...filterParams].join('&')}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getTripsPositions(size: number, page: number, filters: ITripsFilters, searchValue?: string) {
    const filterParams = this.tripsFilterParams(filters, searchValue);
    const path = `/positions?${[`page=${page}`, `size=${size}`, ...filterParams].join('&')}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public exportTrips(filters: ITripsFilters, searchValue?: string, visiblePage?: IListExportPage) {
    const filterParams = this.tripsFilterParams(filters, searchValue);
    const url = this.routerService.mount(`${this.baseURL}${this.exportPath(filterParams, visiblePage)}`);
    return this.createText(null, url);
  }

  private tripsFilterParams(filters: ITripsFilters, searchValue?: string): string[] {
    return this.filterParams(
      filters.selectedClients, filters.selectedOperators, filters.selectedTowns, filters.selectedTypes,
      filters.selectedStatus, searchValue, filters.selectedServices, filters.selectedRoutes,
      undefined, filters.from, filters.to, filters.selectedAvailabilityId,
      filters.selectedDrivers, filters.selectedVehicles, filters.selectedServiceTypes,
    );
  }

  private filterParams(
    expeditionClients: IBase[],
    expeditionOperators: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    searchValue?: string,
    expeditionServices?: IBase[],
    expeditionRoutes?: IBase[],
    dateOnly?: boolean,
    from?: Date,
    to?: Date,
    availabilityId?: number | null,
    expeditionDrivers?: IBase[],
    expeditionVehicles?: IBase[],
    expeditionServiceTypes?: ServiceType[],
  ): string[] {
    const params: string[] = [];
    if (expeditionClients.length > 0) {
      params.push(`clients=${expeditionClients.map(client => client.id).join(',')}`);
    }
    if (expeditionOperators.length > 0) {
      params.push(`operators=${expeditionOperators.map(operator => operator.id).join(',')}`);
    }
    if (expeditionTowns.length > 0) {
      params.push(`towns=${expeditionTowns.map(town => town.id).join(',')}`);
    }
    if (expeditionTypes.length > 0) {
      params.push(`types=${expeditionTypes.join(',')}`);
    }
    if (expeditionStatus.length > 0) {
      params.push(`status=${expeditionStatus.join(',')}`);
    }
    if (expeditionServiceTypes?.length) {
      params.push(`serviceTypes=${expeditionServiceTypes.join(',')}`);
    }
    if (expeditionServices && expeditionServices.length > 0) {
      params.push(`services=${expeditionServices.map(service => service.id).join(',')}`);
    }
    if (expeditionRoutes && expeditionRoutes.length > 0) {
      params.push(`routes=${expeditionRoutes.map(route => route.id).join(',')}`);
    }
    if (expeditionDrivers && expeditionDrivers.length > 0) {
      params.push(`drivers=${expeditionDrivers.map(driver => driver.id).join(',')}`);
    }
    if (expeditionVehicles && expeditionVehicles.length > 0) {
      params.push(`vehicles=${expeditionVehicles.map(vehicle => vehicle.id).join(',')}`);
    }
    if (dateOnly) params.push('date_only=true');
    if (from) params.push(`from=${moment(from).format('YYYY-MM-DD')}`);
    if (to) params.push(`to=${moment(to).format('YYYY-MM-DD')}`);
    if (availabilityId != null) params.push(`availability=${availabilityId}`);
    if (searchValue) params.push(`search=${encodeURIComponent(searchValue)}`);
    return params;
  }

  public getTrip(tripId: number) {
    const path = `/${tripId}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public changeDriver(tripId: number, driverId: number) {
    const path = `/${tripId}/driver/${driverId}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.update({}, url);
  }

  public changeVehicle(tripId: number, driverId: number) {
    const path = `/${tripId}/vehicle/${driverId}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.update({}, url);
  }

  public addPax(tripId: number, nPax: number) {
    const path = `/${tripId}/pax`;
    const requestParam = `n_pax=${nPax}`;
    const url = this.routerService.mount(`${this.baseURL}${path}?${requestParam}`);
    return this.updatePatch({}, url);
  }

  public updateComment(tripId: number, comment: string | null) {
    const path = `/${tripId}/comment`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.updatePatch({ comment }, url);
  }

  public setStatus(tripId: number, status: ExpeditionStatus) {
    const path = `/${tripId}/status`;
    const requestParam = `status=${status}`;
    const url = this.routerService.mount(`${this.baseURL}${path}?${requestParam}`);
    return this.updatePatch({}, url);
  }

  public deleteTrip(tripId: number, data?: any) {
    const body = {
      'id': tripId,
      'comment': data.reason,
      'send_notification': data.sendNotification,
      'refund': data.refund,
      'full_refund': data.fullRefund,
      'cancel_related': data.related
    };
    const url = this.routerService.mount(`${this.baseURL}`);
    return this.delete(url, body);
  }

  public getBookingsByTrip(tripId: number) {
    const path = `/${tripId}/bookings`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getBookingsByStop(tripId: number, stopId: number, pickUps: boolean) {
    const path = `/${tripId}/stop/${stopId}/bookings`;
    const requestParam = `pickups=${pickUps}`;
    const url = this.routerService.mount(`${this.baseURL}${path}?${requestParam}`);
    return this.read(url);
  }

  public getChangeoversByStop(stopId: number) {
    const path = `/stop/${stopId}/changeovers`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getTripPosition(tripId: number) {
    const path = `/${tripId}/real-time-positions`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getTownsPositions(townsIds: number[]) {
    const path = `/real-time-positions?town_ids=${townsIds}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getDrivenRoute(tripId: number) {
    const path = `/${tripId}/position-history`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }
}
